import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

const categoryIcons: Record<string, string> = {
  "cat-shoes-01": "ri-footprint-line",
  "cat-hoodies-02": "ri-t-shirt-line",
  "cat-tshirts-03": "ri-shirt-line",
  "cat-jackets-04": "ri-cloud-windy-line",
  "cat-pants-05": "ri-pantone-line",
  "cat-headwear-06": "ri-glasses-line",
  "cat-sets-07": "ri-stack-line",
  "cat-underwear-08": "ri-checkbox-blank-line",
  "cat-jersey-09": "ri-basketball-line",
  "cat-accessories-10": "ri-vip-crown-line",
  "cat-bags-11": "ri-shopping-bag-line",
  "cat-belts-12": "ri-link",
  "cat-perfumes-13": "ri-contrast-drop-line",
  "cat-other-14": "ri-more-line",
};

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleShoppingGuide = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/?scrollTo=guides");
    } else {
      const el = document.getElementById("shopping-guides");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap cursor-pointer flex-shrink-0"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span className="font-heading text-lg md:text-2xl text-foreground-950">
              GTBuy<span className="text-primary-500"> Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/"
              className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              Home
            </Link>

            <a
              href="#shopping-guides"
              onClick={handleShoppingGuide}
              className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              Shopping Guide
            </a>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-secondary-100 text-foreground-800 text-sm font-medium hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Categories</span>
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 max-h-96 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                        <i className={`${categoryIcons[cat.seq] || "ri-store-line"} text-foreground-500 text-sm`}></i>
                      </div>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              GTBuy Sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Nav */}
          <div className="flex md:hidden items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium cursor-pointer whitespace-nowrap"
            >
              Buy Now
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-800 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-lg ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200 pb-4 pt-2 max-h-[70vh] overflow-y-auto">
            <div className="px-2 space-y-0.5">
              <Link
                to="/"
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-home-4-line text-primary-600 text-sm"></i>
                </div>
                Home
              </Link>

              <a
                href="#shopping-guides"
                onClick={handleShoppingGuide}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-book-open-line text-accent-600 text-sm"></i>
                </div>
                Shopping Guide
              </a>

              <div className="pt-2 pb-1 px-3">
                <span className="text-xs uppercase tracking-wider text-foreground-400 font-medium">
                  Categories
                </span>
              </div>

              {categories.map((cat) => (
                <a
                  key={cat.seq}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-3 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                    <i className={`${categoryIcons[cat.seq] || "ri-store-line"} text-foreground-500 text-sm`}></i>
                  </div>
                  <span>{cat.name}</span>
                </a>
              ))}

              <div className="pt-3 border-t border-background-200 mt-2 px-2 space-y-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="ri-external-link-line text-sm"></i>
                  GTBuy Sheet
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}